/*
Shared "static info page" component — used by About / Privacy / Terms.

Each call site supplies a title, an optional lead paragraph, a list of
sections (heading + raw HTML body), and an optional footer. The helper
builds the same shell for all three so the layout stays identical and
any future tweak — back-button placement, section spacing, link style
— is a single edit.

Why raw HTML strings for section bodies (rather than a structured
content tree): the bodies are mostly hand-authored copy with inline
links, lists, and emphasis. A typed AST would either be over-engineered
or push us back into HTML through a different door. The escape helper
keeps the dynamic title / heading values safe; the bodies are
trusted-author content that can include `<a>` / `<ul>` / `<strong>`.
*/

import { navigate, kPaths } from '../../router'

export interface InfoScreenSection
{
	heading: string
	bodyHtml: string  // trusted markup — author-controlled, never user input
}

export interface InfoScreenContent
{
	title: string
	leadHtml?: string
	sections: InfoScreenSection[]
	footerHtml?: string
}

/*
Mount the screen. `inBackHref` is an optional explicit back target
(defaults to `history.back()` when there's a stack to pop, otherwise
the profile screen — these screens are reached from the Profile
"À propos" card so that's the natural fallback).
*/
export function mountInfoScreen(
	inRoot: HTMLElement,
	inContent: InfoScreenContent,
	inBackHref?: string,
): void
{
	inRoot.innerHTML = `
		<div class="screen info-screen">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">${escapeHtml(inContent.title)}</h1>
			</header>
			<div class="info-screen-body">
				${inContent.leadHtml ? `<p class="info-lead">${inContent.leadHtml}</p>` : ''}
				${inContent.sections.map((inS) => `
					<section class="info-section">
						<h2 class="info-section-title">${escapeHtml(inS.heading)}</h2>
						<div class="info-section-body">${inS.bodyHtml}</div>
					</section>
				`).join('')}
				${inContent.footerHtml ? `<div class="info-footer">${inContent.footerHtml}</div>` : ''}
			</div>
		</div>
	`
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => {
		if (inBackHref) navigate(inBackHref)
		else if (history.length > 1) history.back()
		else navigate(kPaths.profile())
	})
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
